import React from 'react';
import { ChatMessageItem, DisplayableSearchResult } from '../types';
import { ProductCard } from './ProductCard';
import { WebpageCard } from './WebpageCard';
import { addUtmParametersToUrl } from '../utils';
import botAvatar from '../assets/primeo_energie.svg';

interface ChatMessageProps {
    message: ChatMessageItem;
    onCardHover: (productId: string | null) => void;
    onToggleProductsVisibility: (messageId: string) => void;
}

// Helper to parse markdown content
const parseInlineContent = (text: string): React.ReactNode[] => {
    const parts: React.ReactNode[] = [];
    let lastIndex = 0;
    const combinedRegex = /(\[([^\]]+?)\]\(([^)]+)\))|(https?:\/\/[^\s<>"'.]+(?:[^\s<>"'.,?!]*[^\s<>"'.,?!)]|[a-zA-Z0-9\/#_=+&%-~]))|(\*\*(.+?)\*\*)/g;

    let match;
    while ((match = combinedRegex.exec(text)) !== null) {
        if (match.index > lastIndex) {
            parts.push(text.substring(lastIndex, match.index));
        }
        const key = `${match[0]}-${match.index}`;
        
        if (match[2] && match[3]) {
            const linkText = match[2];
            const originalUrl = match[3];
            const finalUrl = addUtmParametersToUrl(originalUrl);
            parts.push(
                <a
                    key={key} href={finalUrl} target={'_blank'} rel={'noopener noreferrer'}
                    className="inline-flex items-center justify-center bg-[#0066CC] hover:bg-[#004499] text-white text-xs font-medium py-1.5 px-3 rounded-lg transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0066CC] mx-0.5 my-1 shadow-sm hover:shadow-md"
                    style={{ textDecoration: 'none' }} >
                    {linkText}
                </a>
            );
        } else if (match[4] && match[4].startsWith('http')) {
            const urlWithUtm = addUtmParametersToUrl(match[4]);
            parts.push(
                <a key={key} href={urlWithUtm} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center justify-center bg-[#0066CC] hover:bg-[#004499] text-white text-xs font-medium py-1.5 px-3 rounded-lg transition-colors duration-150 ease-in-out focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#0066CC] mx-0.5 my-1 shadow-sm hover:shadow-md"
                    style={{ textDecoration: 'none' }}>
                    Visit Link
                </a>
            );
        } else if (match[6]) {
            parts.push(<strong key={key} className="font-semibold">{match[6]}</strong>);
        }
        lastIndex = combinedRegex.lastIndex;
    }
    if (lastIndex < text.length) {
        parts.push(text.substring(lastIndex, text.length));
    }
    return parts;
};

const renderMarkdownContent = (text: string): React.ReactNode => {
    const lines = text.split('\n');
    const elements: React.ReactNode[] = [];
    let listItems: React.ReactNode[] = [];
    let listKey = 0;

    const flushList = () => {
        if (listItems.length > 0) {
            elements.push(
                <ul key={`ul-${listKey++}`} className="list-disc list-inside space-y-1 my-1 pl-2">
                    {listItems}
                </ul>
            );
            listItems = [];
        }
    };

    lines.forEach((line, index) => {
        if (line.trim() === '') {
            flushList();
            elements.push(<div key={`br-${index}`} className="h-2" />);
            return;
        }
        const listMatch = line.match(/^\s*[\*\-]\s+(.*)/);
        if (listMatch) {
            listItems.push(<li key={`li-${index}`}>{parseInlineContent(listMatch[1])}</li>);
        } else {
            flushList();
            elements.push(<div key={`p-${index}`}>{parseInlineContent(line)}</div>);
        }
    });

    flushList();

    return elements.length === 1 ? elements[0] : <>{elements}</>;
};

export const ChatMessage: React.FC<ChatMessageProps> = ({ 
    message, 
    onCardHover,
    onToggleProductsVisibility
}) => {
    const { sender, unifiedSearchResults, searchResultLayout, areProductsVisible } = message;

    const processedContent = (typeof message.content === 'string')
        ? renderMarkdownContent(message.content)
        : message.content;

    const senderClass = sender === 'user' ? 'items-end' : 'items-start';
    const bubbleClass = sender === 'user' 
        ? 'bg-[#0066CC] text-white' 
        : sender === 'bot' 
        ? 'bg-gray-200 text-gray-800'
        : 'bg-blue-100 text-blue-700 border border-blue-300';
    
    const icon = sender === 'user' 
        ? <UserIcon className="w-7 h-7 text-white bg-[#0066CC] rounded-full p-1" /> 
        : sender === 'bot'
        ? <img src={botAvatar} alt="Bot Avatar" className="w-7 h-7 rounded-full object-cover" />
        : <SystemIcon className="w-7 h-7 text-blue-600 bg-blue-200 rounded-full p-1" />;

    const messageAlignment = sender === 'user' ? 'flex-row-reverse space-x-reverse' : 'flex-row';
    
    const hasStructuredContent = !!unifiedSearchResults;
    const contentContainerClass = hasStructuredContent ? 'w-full' : 'max-w-xs sm:max-w-md md:max-w-lg lg:max-w-xl';
    
    const searchResultsDisplay = !message.isStreaming && unifiedSearchResults && (
        <div className="mt-3">
            {(searchResultLayout === 'product-first' || areProductsVisible) && unifiedSearchResults.products.length > 0 && (
                <div className="grid grid-cols-2 gap-2 sm:gap-3 auto-rows-fr mt-2">
                    {unifiedSearchResults.products.map(product => (
                        <ProductCard 
                            key={product.id}
                            product={product}
                            onCardHover={onCardHover}
                        />
                    ))}
                </div>
            )}
            
            {unifiedSearchResults.webpages.length > 0 && (
                <div className="flex flex-wrap gap-2 mt-2">
                    {unifiedSearchResults.webpages.map(page => (
                        <WebpageCard key={page.id} webpage={page} />
                    ))}
                </div>
            )}
        </div>
    );

    if (sender === 'system') {
        return (
            <div className="flex justify-center my-2">
                <div className={`flex items-center space-x-2 text-xs p-2 rounded-lg shadow-sm ${bubbleClass} whitespace-pre-wrap`}>
                    {icon}
                    <span>{processedContent}</span>
                </div>
            </div>
        );
    }

    return (
        <div className={`flex flex-col ${senderClass}`}>
            <div className={`flex items-start space-x-2 ${messageAlignment}`}>
                <div className="flex-shrink-0 mt-1">{icon}</div>
                <div className={`p-3 rounded-lg shadow-md ${bubbleClass} ${contentContainerClass}`}>
                    <div className="whitespace-pre-wrap break-words">
                        {processedContent}
                    </div>
                    {searchResultsDisplay}
                </div>
            </div>
        </div>
    );
};

const UserIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
    </svg>
);

const SystemIcon: React.FC<{ className?: string }> = ({ className }) => (
    <svg className={className} fill="currentColor" viewBox="0 0 24 24">
        <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
    </svg>
);
